import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  calculateCommercialAccessRefreshSchedule,
  commercialAccessRedirectPath,
  commercialAccessRetryDelayMs,
  shouldRefreshForLifecycleEvent,
  unauthenticatedEntryPath
} from "./commercialAccessRefresh";
import { useSessionBootstrap } from "./SessionBootstrapProvider";

type ProtectedRouteStatus = "checking" | "authenticated" | "redirecting" | "error";

export function useProtectedRouteSession(tenantSlug: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    status,
    session,
    sessionReceivedAtMs,
    errorMessage,
    verifySession,
    refreshSessionSilently
  } = useSessionBootstrap();
  const [routeStatus, setRouteStatus] = useState<ProtectedRouteStatus>("checking");
  const lastBoundaryRefreshAtRef = useRef<number | null>(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    lastBoundaryRefreshAtRef.current = null;
  }, [tenantSlug]);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      setRouteStatus("checking");
      const result = await verifySession(tenantSlug);
      if (cancelled) return;

      if (result.status === "authenticated") {
        const currentLocation = locationRef.current;
        const redirect = commercialAccessRedirectPath({
          tenantSlug,
          pathname: currentLocation.pathname,
          accessMode: result.session.commercialAccess.accessMode
        });
        if (redirect) {
          setRouteStatus("redirecting");
          navigate(redirect, { replace: true });
          return;
        }
        setRouteStatus("authenticated");
        return;
      }

      if (result.status === "unauthenticated") {
        setRouteStatus("redirecting");
        const currentLocation = locationRef.current;
        navigate(unauthenticatedEntryPath({
          tenantSlug,
          pathname: currentLocation.pathname,
          search: currentLocation.search
        }), { replace: true });
        return;
      }
      setRouteStatus("error");
    }

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, [navigate, tenantSlug, verifySession]);

  useEffect(() => {
    if (status !== "authenticated" || !session || session.tenant.tenantSlug !== tenantSlug) return;
    const redirect = commercialAccessRedirectPath({
      tenantSlug,
      pathname: location.pathname,
      accessMode: session.commercialAccess.accessMode
    });
    if (redirect) {
      setRouteStatus("redirecting");
      navigate(redirect, { replace: true });
    } else {
      setRouteStatus("authenticated");
    }
  }, [location.pathname, navigate, session, status, tenantSlug]);

  useEffect(() => {
    if (status !== "unauthenticated") return;
    setRouteStatus("redirecting");
    navigate(unauthenticatedEntryPath({
      tenantSlug,
      pathname: location.pathname,
      search: location.search
    }), { replace: true });
  }, [location.pathname, location.search, navigate, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !session || session.tenant.tenantSlug !== tenantSlug) return;

    let disposed = false;
    let boundaryTimer: number | null = null;
    let retryTimer: number | null = null;
    let transientFailureCount = 0;
    const receivedAtMs = sessionReceivedAtMs ?? Date.now();

    const clearRetry = () => {
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      retryTimer = null;
    };

    const routeEntry = () => {
      setRouteStatus("redirecting");
      navigate(unauthenticatedEntryPath({
        tenantSlug,
        pathname: location.pathname,
        search: location.search
      }), { replace: true });
    };

    const scheduleRetry = () => {
      if (disposed || retryTimer !== null) return;
      transientFailureCount += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        void triggerRefresh("retry");
      }, commercialAccessRetryDelayMs(transientFailureCount));
    };

    const triggerRefresh = async (source: "boundary" | "focus" | "visibility" | "pageshow" | "retry") => {
      clearRetry();
      if (source === "boundary") lastBoundaryRefreshAtRef.current = Date.now();
      const result = await refreshSessionSilently(tenantSlug);
      if (disposed) return;
      if (result.status === "unauthenticated") {
        routeEntry();
        return;
      }
      if (result.status === "error") {
        scheduleRetry();
        return;
      }
      transientFailureCount = 0;
      const redirect = commercialAccessRedirectPath({
        tenantSlug,
        pathname: location.pathname,
        accessMode: result.session.commercialAccess.accessMode
      });
      if (redirect) {
        setRouteStatus("redirecting");
        navigate(redirect, { replace: true });
      } else {
        setRouteStatus("authenticated");
      }
    };

    const handleFocus = () => {
      if (shouldRefreshForLifecycleEvent("focus")) void triggerRefresh("focus");
    };
    const handleVisibilityChange = () => {
      if (shouldRefreshForLifecycleEvent("visibilitychange", document.visibilityState)) {
        void triggerRefresh("visibility");
      }
    };
    const handlePageShow = () => {
      if (shouldRefreshForLifecycleEvent("pageshow")) void triggerRefresh("pageshow");
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);

    const schedule = calculateCommercialAccessRefreshSchedule({
      commercialAccess: session.commercialAccess,
      clientNowMs: Date.now(),
      receivedAtMs,
      lastBoundaryRefreshAtMs: lastBoundaryRefreshAtRef.current
    });
    if (schedule.action === "refreshNow") {
      void triggerRefresh("boundary");
    } else if (schedule.action === "schedule") {
      boundaryTimer = window.setTimeout(() => {
        boundaryTimer = null;
        void triggerRefresh("boundary");
      }, schedule.delayMs);
    }

    return () => {
      disposed = true;
      if (boundaryTimer !== null) window.clearTimeout(boundaryTimer);
      clearRetry();
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [
    location.pathname,
    location.search,
    navigate,
    refreshSessionSilently,
    session?.commercialAccess.accessMode,
    session?.commercialAccess.evaluatedAt,
    session?.commercialAccess.nextRecheckAt,
    session?.commercialAccess.serviceEndsAt,
    session?.commercialAccess.storedContractStatus,
    session?.tenant.tenantSlug,
    sessionReceivedAtMs,
    status,
    tenantSlug
  ]);

  return {
    routeStatus,
    sessionStatus: status,
    session,
    errorMessage,
    isSessionReady: routeStatus === "authenticated" && status === "authenticated"
  };
}
